import { Role } from '../constants/roles';

/**
 * Check if a user with given roles can access a specific route
 * @param pathname - The URL path to check
 * @param userRoles - Array of user roles
 * @param permissions - Optional array of permission codes from API
 * @returns boolean indicating if access is allowed
 */
// Map routes to required permissions
const ROUTE_PERMISSION_MAP: Record<string, string[]> = {
  '/dashboard': ['dashboard.view'],
  '/central-de-solicitacoes': ['intake-requests.view'],
  '/manutencao-corretiva': ['corrective-maintenance.view'],
  '/manutencao-corretiva/condominio': ['corrective-maintenance.view'],
  '/manutencao-corretiva/morador': ['corrective-maintenance.view'],
  '/manutencao-preventiva': ['maintenance.view'],
  '/manutencao-preventiva/condominio': ['maintenance.view', 'maintenance.condominium.access'],
  '/manutencao-preventiva/condominio/plano-de-manutencao': ['maintenance.view', 'maintenance.plan.view'],
  '/manutencao-preventiva/condominio/historico-das-manutencoes': ['maintenance.view', 'maintenance.history.view'],
  '/manutencao-preventiva/condominio/mapa-de-risco': ['maintenance.view', 'maintenance.condominium.access'],
  '/manutencao-preventiva/condominio/construtoras': [],
  '/manutencao-preventiva/condomino': ['maintenance.view', 'maintenance.view.own'],
  '/manutencao-preventiva/condomino/plano-de-manutencao': ['maintenance.view', 'maintenance.plan.view.own'],
  '/manutencao-preventiva/condomino/historico-das-manutencoes': ['maintenance.view', 'maintenance.history.view.own'],
  '/agenda-sindico': ['social.manager-agenda.view'],
  '/social': ['social.access'],
  '/social/agenda-sindico': ['social.manager-agenda.view'], // Legacy route
  '/social/agenda/reservas': ['reservations.access', 'reservations.view'], // Legacy route
  '/social/suporte-tecnico': ['tickets.view', 'support.technical.access'],
  '/social/reclamacoes---sugestoes': ['tickets.view'],
  '/social/assembleias': ['assemblies.view'],
  '/social/assembleias-virtuais': ['assemblies.view'],
  '/social/meus-pertences': ['belongings.view.own'],
  '/social/anuncios-condominos': ['announcements.residents.view'],
  '/social/anuncios-comercio-local': ['announcements.commerce.view'],
  '/social/sugestoes': ['suggestions.view'],
  '/portaria-e-seguranca': ['security.access'],
  '/portaria-e-seguranca/check-in-rapido': ['security.checkin.view'],
  '/portaria-e-seguranca/check-in-out': ['security.checkin.view'],
  '/portaria-e-seguranca/controle-de-encomendas': ['deliveries.manage'],
  '/portaria-e-seguranca/cadastro-de-visitantes': ['visitors.manage'],
  '/portaria-e-seguranca/ocorrencias-de-seguranca': ['security.incidents.view'],
  '/portaria-e-seguranca/patrimonios': ['assets.view'],
  '/portaria-e-seguranca/ensaio-de-emergencia': ['emergency.drills.view'],
  '/portaria-e-seguranca/relatorios-checkin': ['security.checkin.view'],
  '/portaria-e-seguranca/achados-e-perdidos': ['lost-and-found.view'],
  // Controle de Acesso (Segware)
  // O morador tem `access-control.doors.open` (abrir portas) e
  // `access-control.events.view` (a visão global dos acessos): entra na rota
  // índice, em /portas e em /eventos. Dashboard e Configuração continuam
  // exclusivos de `access-control.view`/`.manage`.
  '/controle-de-acesso': [
    'access-control.view',
    'access-control.doors.open',
    'access-control.events.view',
  ],
  '/controle-de-acesso/dashboard': ['access-control.view'],
  // A visão de acessos é global (todo o condomínio) e o morador a enxerga via
  // `access-control.events.view`. Dashboard e Configuração seguem restritos.
  '/controle-de-acesso/eventos': [
    'access-control.view',
    'access-control.events.view',
  ],
  '/controle-de-acesso/moradores': ['access-control.view'], // Legacy redirect

  '/controle-de-acesso/portas': [
    'access-control.view',
    'access-control.doors.open',
  ],
  '/controle-de-acesso/configuracao': ['access-control.manage'],
  // Cadastro de pessoas no controle de acesso. O morador entra pelo escopo
  // `.own`: vê e cadastra apenas os visitantes/prestadores dele.
  '/controle-de-acesso/pessoas': [
    'access-control.people.view',
    'access-control.people.view.own',
  ],
  '/controle-de-acesso/pessoas/nova': [
    'access-control.people.manage',
    'access-control.people.manage.own',
  ],
  '/prestadores-de-servicos': ['service-providers.access'],
  '/prestadores-de-servicos/condominio': ['service-providers.condominium.access'],
  '/prestadores-de-servicos/condominio/elevadores': ['service-providers.elevators.view'],
  '/prestadores-de-servicos/condominio/vigilancia-eletronica': ['service-providers.surveillance.view'],
  '/prestadores-de-servicos/condominio/empresa-de-limpeza': ['service-providers.cleaning.view'],
  '/prestadores-de-servicos/condominio/empresa-de-sistema-a-gas': ['service-providers.gas.view'],
  '/prestadores-de-servicos/condominio/empresa-de-sistemas-de-combate-a-incendio': ['service-providers.firefighting.view'],
  '/prestadores-de-servicos/condominio/empresa-de-jardinagem': ['service-providers.gardening.view'],
  '/prestadores-de-servicos/condomino': ['service-providers.resident.access'],
  '/prestadores-de-servicos/condomino/assistencias-tecnicas': ['service-providers.technical.view'],
  '/prestadores-de-servicos/condomino/aulas': ['courses.view'],
  '/prestadores-de-servicos/condomino/automoveis': ['service-providers.automotive.view'],
  '/prestadores-de-servicos/condomino/consultoria': ['service-providers.consulting.view'],
  '/prestadores-de-servicos/condomino/design-e-tecnologia': ['service-providers.design-tech.view'],
  '/prestadores-de-servicos/condomino/eventos': ['service-providers.events.view'],
  '/prestadores-de-servicos/condomino/moda-e-beleza': ['service-providers.beauty.view'],
  '/prestadores-de-servicos/condomino/reformas-e-reparos': ['service-providers.repairs.view'],
  '/prestadores-de-servicos/condomino/saude': ['service-providers.health.view'],
  '/prestadores-de-servicos/condomino/servicos-domesticos': ['service-providers.domestic.view'],
  '/governanca': ['governance.access'],
  '/governanca/manutencoes': ['governance.maintenance.view'],
  '/governanca/prestadores-de-servico': ['governance.maintenance.view'],
  '/governanca/rotinas-do-zelador': ['governance.maintenance.view'],
  '/governanca/comunicacoes': ['governance.maintenance.view'],
  '/governanca/eficiencia-operacional': ['governance.maintenance.view'],
  '/governanca/social': ['governance.maintenance.view'],
  '/governanca/governanca-de-manutencoes': ['governance.maintenance.view'],
  '/governanca/documentos': ['governance.maintenance.view'],
  '/governanca/portaria-e-seguranca': ['governance.maintenance.view'],
  '/governanca/governanca-global': ['admin.governance.view'],
  '/governanca/condomino': ['governance.resident.view'],
  '/comunicacoes': ['communication.access'],
  '/comunicacoes/comunicados-e-avisos': ['communication.notices.view'],
  '/comunicacoes/enquetes': ['polls.view'],
  '/comunicacoes/enquetes-e-votacoes': ['polls.view'],
  '/comunicacoes/assembleias-virtuais': ['assemblies.view'], // Legacy route
  '/construtora': [],
  '/construtora/projetos': [],
  '/construtora/orcamentos': [],
  '/construtora/comunicacoes': [],
  '/rotinas-do-zelador': ['janitor.routines.view', 'janitor.routines.access'],
  '/reservas': ['reservations.access'],
  '/reservas/reserva-de-areas': ['reservations.view', 'reservations.view.own'],
  '/reservas/areas-comuns': ['reservations.access', 'reservations.view'],
  '/reservas/historico-de-reservas': ['reservations.view', 'reservations.view.own'], // Legacy route
  '/reservas/regras-de-uso': ['reservations.view', 'reservations.view.own'], // Legacy route
  '/documentos': ['documents.view'],
  '/documentos/regulamentos-internos': ['documents.view'],
  '/documentos/atas-de-reunioes': ['documents.view'],
  '/documentos/contratos': ['documents.view'],
  '/documentos/convencao-do-condominio': ['documents.view'],
  '/documentos/regras-de-utilizacao-de-areas-comuns': ['documents.view'],
  '/documentos/normas-de-seguranca': ['documents.view'],
  '/documentos/inscricao-do-edificio-na-receita-federal-com-o-cartao-cnpj-atualizado': ['documents.view'],
  '/documentos/lei-do-condominio': ['documents.view'],
  '/documentos/politica-de-privacidade-do-condominio': ['documents.view'],
  '/documentos/planta-baixa-da-area-contruida-1-copia-em-papel-impresso-1-copia-digital': ['documents.view'],
  '/configuracoes': ['settings.access'],
  '/configuracoes/notificacoes-e-preferencias': ['settings.notifications.view'],
  '/configuracoes/configuracao-do-condominio': ['settings.condominium.view'],
  '/configuracoes/permissoes-de-acesso': ['settings.permissions.view'],
  '/configuracoes/cadastros-de-usuario': ['social.registrations.view'],
  '/configuracoes/meus-dados': ['users.profile.edit.own'],
  '/configuracoes/prestadores-servicos': ['service-providers.access', 'service-providers.condominium.access'],
  '/configuracoes/areas-comuns': ['reservations.access', 'reservations.view'], // Legacy route
  // Gestão de Condomínios (Admin MEP & Síndico)
  '/gestao-condominios': ['establishments.manage', 'establishments.manage.own'],
  '/gestao-condominios/admin': ['establishments.manage'],
  '/gestao-condominios/meus': ['establishments.manage.own'],
  // Configurações Administrativas (Admin MEP only)
  '/configuracoes-administrativas': ['admin.settings.access'],
  '/configuracoes-administrativas/gestao-condominios': ['establishments.manage', 'admin.settings.access'],
  '/configuracoes-administrativas/gestao-usuarios': ['admin.syndics.manage', 'establishments.manage'],
  '/configuracoes-administrativas/governanca-global': ['admin.governance.view'],
  // EAD (Educação a Distância)
  '/ead': ['courses.view'],
  // Agenda do Prestador de Serviços
  '/prestador/agenda': ['service-provider.agenda.access'],
};

// Dynamic route patterns mapped to required permissions
// Use permissive array: if user has ANY of listed permissions, allow.
const ROUTE_PERMISSION_PATTERNS: Array<{ pattern: RegExp; required: string[] }> = [
  {
    // Condominium maintenance history with maintenanceId (Admin/Manager view)
    pattern: /^\/manutencao-preventiva\/maintenance\/[^/]+\/history$/, // /manutencao-preventiva/maintenance/:id/history
    required: ['maintenance.view', 'maintenance.history.view'],
  },
  {
    // Resident maintenance history with maintenanceId
    pattern: /^\/manutencao-preventiva\/condomino\/maintenance\/[^/]+\/history$/, // /manutencao-preventiva/condomino/maintenance/:id/history
    required: ['maintenance.history.view.own', 'maintenance.history.view'],
  },
  {
    // EAD course details
    pattern: /^\/ead\/[^/]+$/,
    required: ['courses.view'],
  },
  {
    // EAD lesson content
    pattern: /^\/ead\/[^/]+\/lessons\/[^/]+$/,
    required: ['courses.view'],
  },
  {
    // Edição de condomínio (fluxo de síndico/admin)
    pattern: /^\/condominios\/[^/]+\/editar$/,
    required: ['establishments.manage', 'establishments.manage.own'],
  },
  {
    // Detalhes de condomínio (visualização)
    pattern: /^\/condominios\/[^/]+$/,
    required: ['establishments.manage', 'establishments.manage.own'],
  },
];

export const checkRoutePermission = (pathname: string, userRoles: Role[], permissions?: string[]): boolean => {
  // Allow access to /403 page without further checks
  if (pathname === '/403' || pathname === '/404' || pathname === '/login' || pathname === '/' || pathname === '') {
    return true;
  }
  
  // Use permissions from API if provided
  if (permissions && permissions.length > 0) {
    // Check if route has specific permission requirements
    const requiredPermissions = ROUTE_PERMISSION_MAP[pathname];
    
    if (requiredPermissions) {
      // Empty array = allow any authenticated user
      if (requiredPermissions.length === 0) {
        return true;
      }
      // User needs at least one of the required permissions
      return requiredPermissions.some(requiredPerm =>
        permissions.includes(requiredPerm)
      );
    }

    // Check dynamic patterns
    for (const { pattern, required } of ROUTE_PERMISSION_PATTERNS) {
      if (pattern.test(pathname)) {
        return required.some(req => permissions.includes(req));
      }
    }
    
    // For routes not in the map, check if user has any permission that matches the path pattern
    // This is a fallback for dynamic routes
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // Try different permission patterns
    const possiblePermissions = [
      pathSegments.join('.') + '.view',
      pathSegments.join('.'),
      pathSegments[0] + '.view',
      pathSegments[0] + '.access',
    ];
    
    return possiblePermissions.some(possiblePerm => 
      permissions.some(userPerm => 
        userPerm === possiblePerm || possiblePerm.startsWith(userPerm + '.')
      )
    );
  }
  
  // No permissions available means no access
  return false;
};

/**
 * Check if a user with given roles can view a specific permission code
 * @param userRoles - Array of user roles
 * @param code - Permission code to check
 * @param permissions - Optional array of permission codes from API
 * @returns boolean indicating if view is allowed
 */
export const canView = (userRoles: Role[], code: string, permissions?: string[]): boolean => {
  // If no code is provided, deny access
  if (!code) {
    return false;
  }
  
  // Use permissions from API if provided
  if (permissions && permissions.length > 0) {
    return permissions.some(perm => {
      // Exact match or parent permission
      return perm === code || code.startsWith(perm + '.');
    });
  }
  
  // No permissions available means no access
  return false;
};

/**
 * Check if a user with given roles can edit a specific permission code
 * @param userRoles - Array of user roles
 * @param code - Permission code to check
 * @param permissions - Optional array of permission codes from API
 * @returns boolean indicating if edit is allowed
 */
export const canEdit = (userRoles: Role[], code: string, permissions?: string[]): boolean => {
  // Use permissions from API if provided
  if (permissions && permissions.length > 0) {
    // For edit permissions, we might need a more specific check
    // For now, we'll assume if they have view permission, they can edit
    // This should be refined based on actual API permission structure
    return permissions.some(perm => {
      // Exact match or parent permission
      return perm === code || code.startsWith(perm + '.');
    });
  }
  
  // No permissions available means no access
  return false;
};

// Legacy functions removed - all permission checking now relies on backend API
